import { Header } from "@app/components/Header";
import { Footer } from "@app/components/Footer";
import { SidebarNavigation } from "@app/components/SidebarNavigation";
import OnboardingWizard from "@app/components/OnboardingWizard";
import DemoTour from "@app/components/demo-tour/DemoTour";
import DemoBanner from "@app/components/DemoBanner";
import ReadonlyScopeBanner from "@app/components/ReadonlyScopeBanner";
import OrganisationContextBanner from "@app/components/organisation/OrganisationContextBanner";
import AuthSetupBanner from "@app/components/AuthSetupBanner";
import API from "@app/services/api";
import useUser from "@app/zustand/user";
import useMageXStore, { EMPTY_FILTER_PARAMS } from "@app/zustand/store";
import { dashboardPathwayStore } from "@app/zustand/dashboard-pathway";
import { useScope } from "@app/zustand/scope";
import { usePreviewView } from "@app/zustand/preview-view";
import useFieldVisibility from "@app/zustand/field-visibility";
import { Outlet } from "react-router";
import { useEffect, useState } from "react";
import type { SpecialityDescriptor } from "@app/utils/speciality";
import { isDemoEmail } from "@app/utils/demo-account";

interface UserConnectionItem {
  id: string;
  tenant_id: string;
  url: string;
  has_credentials: boolean;
}

interface ConfigData {
  specialite?: string | null;
  supported_specialites?: SpecialityDescriptor[];
  user_connections?: UserConnectionItem[];
}

export default function HomeLayout() {
  const user = useUser((state) => state.user);
  const IS_DEMO = isDemoEmail((user as { email?: string } | null)?.email);
  const previewRole = usePreviewView((state) => state.previewRole);
  const setPreview = usePreviewView((state) => state.setPreview);
  const hydrated = useMageXStore((state) => state._hasHydrated);
  const lastUserId = useMageXStore((state) => state.lastUserId);
  const setLastUserId = useMageXStore((state) => state.setLastUserId);
  const setFilterParams = useMageXStore((state) => state.setFilterParams);
  const setPatientsSelect = useMageXStore((state) => state.setPatientsSelect);
  const setPractitionerFilter = useMageXStore(
    (state) => state.setPractitionerFilter,
  );
  const setScope = useScope((state) => state.setScope);
  const loadFieldVisibility = useFieldVisibility((state) => state.load);
  const resetFieldVisibility = useFieldVisibility((state) => state.reset);

  // Loaded once here rather than per surface: the dashboard, the patient page and the
  // configuration panel all read the same answer, and fetching it in each would make a
  // field flash on screen before disappearing on every navigation.
  useEffect(() => {
    if (!user) return;
    void loadFieldVisibility();
  }, [user, loadFieldVisibility]);

  // Reset persisted cross-user state on login or user-switch. Without this,
  // a period set by a previous user leaks into the next session and silently
  // narrows their dashboard.
  // ``lastUserId`` is itself persisted in IndexedDB so the comparison survives
  // page reloads: same id → no reset, different id → reset + record new id.
  // Compared only once IndexedDB has answered: the board and the scope reset below are stored
  // elsewhere, and read early a reload would wipe them, whatever the timing on a given machine.
  useEffect(() => {
    const currentId = (user as { id?: string } | null)?.id ?? null;
    if (!hydrated || !currentId || currentId === lastUserId) return;
    setFilterParams(EMPTY_FILTER_PARAMS);
    setPractitionerFilter(null);
    setPatientsSelect([]);
    setPreview(null);
    // The board and the scope are the previous reader's too: their conditions would narrow this
    // reader's board in silence, and their cards would be saved as this reader's (`useBoardSync`).
    dashboardPathwayStore.getState().reset();
    setScope("mine");
    // Field visibility is per practitioner. It is not persisted, but an in-app user switch
    // never remounts the store, so the previous doctor's hidden fields would stay hidden
    // for the next one until a hard reload. Dropping it re-triggers the load effect above.
    resetFieldVisibility();
    setLastUserId(currentId);
  }, [
    user,
    hydrated,
    lastUserId,
    setFilterParams,
    setPractitionerFilter,
    setPatientsSelect,
    setPreview,
    setScope,
    resetFieldVisibility,
    setLastUserId,
  ]);

  // On demo login, default to medecin view if no role is set yet.
  useEffect(() => {
    if (IS_DEMO && previewRole == null) {
      setPreview({ role: "medecin" });
    }
  }, [IS_DEMO, previewRole, setPreview]);

  const [sidebarWidth, setSidebarWidth] = useState("16rem");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingConfig, setOnboardingConfig] = useState<ConfigData>({});

  useEffect(() => {
    // Listen for sidebar width changes
    const updateWidth = () => {
      const width = getComputedStyle(document.documentElement)
        .getPropertyValue("--sidebar-width")
        .trim();
      if (width) {
        setSidebarWidth(width);
      }
    };

    updateWidth();
    const interval = setInterval(updateWidth, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Check if onboarding is needed (no specialite set)
    if (IS_DEMO) return;
    const dismissed = sessionStorage.getItem("onboarding_dismissed") === "1";
    if (dismissed) return;

    API.get({ path: "/configuration/" })
      .then((res: unknown) => {
        const r = res as { ok: boolean; data?: ConfigData };
        if (r.ok && r.data) {
          const { specialite, user_connections } = r.data;
          setOnboardingConfig({
            specialite: specialite ?? null,
            supported_specialites: r.data.supported_specialites ?? [],
            user_connections: user_connections ?? [],
          });
          if (!specialite) {
            setShowOnboarding(true);
          }
        }
      })
      .catch(() => {
        // Silently ignore — don't block app access on config fetch failure
      });
  }, [IS_DEMO]);

  const handleSpecialiteChosen = async (id: string) => {
    const res = (await API.patch({
      path: "/configuration/profile",
      body: { specialite: id },
    })) as { ok: boolean };
    if (res.ok) {
      setOnboardingConfig((prev) => ({ ...prev, specialite: id }));
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem("onboarding_dismissed", "1");
    setShowOnboarding(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex">
      <SidebarNavigation />
      {/* min-w-0: otherwise a wide table stretches the column past the viewport and
          the whole page, header included, scrolls sideways under the sidebar.
          The margin alone is animated: `transition-all` also ran flex-grow up from 0
          on load, and whatever measured the column then got a few pixels. */}
      <div
        className="flex-1 min-w-0 flex flex-col transition-[margin-left] duration-300 ease-in-out"
        style={{ marginLeft: sidebarWidth }}
      >
        {IS_DEMO && <DemoBanner />}
        <ReadonlyScopeBanner />
        <OrganisationContextBanner />
        <AuthSetupBanner />
        <Header />
        <div className="relative flex-1 overflow-auto">
          <Outlet />
        </div>
        <Footer />
      </div>

      {showOnboarding && (
        <OnboardingWizard
          userConnections={onboardingConfig.user_connections ?? []}
          specialite={onboardingConfig.specialite ?? null}
          supportedSpecialites={onboardingConfig.supported_specialites ?? []}
          onSpecialiteChosen={handleSpecialiteChosen}
          onDismiss={handleDismiss}
        />
      )}

      {IS_DEMO && <DemoTour />}
    </div>
  );
}
